// MA-395 — one-shot reconnect retry for a guarded assignedSkillIds push.
// Literal mirror of levelsPushRetry.ts's pushLevelsWithRetry (same "one
// reconnect mechanism, reused at both call sites" rationale) — the two stay
// separate files because they wrap different guarded writers with different
// payload shapes (updateAssignedSkillIdsSafe additionally needs
// cloudMirror, which updateStudentLevelsSafe has no analogue for).
//
// updateAssignedSkillIdsSafe (supabaseClient.js) is safe to retry with the
// identical payload any number of times from any caller: an identical
// re-push either fails the same guard again (fine, no-op) or succeeds once
// (fine). On failure this registers a single registerOnlineCallback retry
// of the SAME payload and unregisters itself once that retry fires (success
// or failure) — it only covers "still in this tab, reconnects soon"; a
// later app boot re-deriving "local still newer" (App/assignedSkillsPullMerge.js)
// is the durable fallback for a tab that closes before reconnecting.
// Rejects with the original error so callers keep their own error UI/telemetry.

import { updateAssignedSkillIdsSafe } from "@/supabaseClient.js";
import { registerOnlineCallback } from "@/lib/reachability";

export interface AssignedSkillsPushPayload {
  assignedSkillIds: string[];
  assignedSkillIdsUpdatedAt: string;
  cloudMirror: string[] | undefined;
}

export interface AssignedSkillsPushDeps {
  updateAssignedSkills: typeof updateAssignedSkillIdsSafe;
  onReconnect: typeof registerOnlineCallback;
}

const defaultDeps: AssignedSkillsPushDeps = {
  updateAssignedSkills: updateAssignedSkillIdsSafe,
  onReconnect: registerOnlineCallback,
};

export function pushAssignedSkillsWithRetry(
  studentId: string,
  payload: AssignedSkillsPushPayload,
  deps: AssignedSkillsPushDeps = defaultDeps,
): Promise<unknown> {
  return deps.updateAssignedSkills(studentId, payload).catch((err: unknown) => {
    const unregister = deps.onReconnect(() => {
      unregister();
      void deps.updateAssignedSkills(studentId, payload).catch(() => {});
    });
    throw err;
  });
}
